import {
  IQuoteItemWithDisplayType,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
} from '@monetize/types/app';
import { Control, useWatch, UseFormSetValue } from 'react-hook-form';
import {
  QuoteRenewalUpliftFormType,
  UpliftEligibleOffering,
} from '../../../utils/quoteRenewalUplift.utils';
import { MBox, MCheckbox, MGrid, MGridItem, MText } from '../../Monetize';
import { QuoteRenewalUpliftProductRow } from './QuoteRenewalUpliftProductRow';

/** Offering / product, current PPU, renewal PPU */
export const UPLIFT_GRID_COLUMNS = '3fr 2fr 2fr';
/** Checkbox, offering / product, uplift %, current PPU, renewal PPU */
export const UPLIFT_GRID_COLUMNS_INDIVIDUAL = 'auto 3fr 1.5fr 2fr 2fr';

export const getUpliftGridColumns = (isIndividualMode: boolean) =>
  isIndividualMode ? UPLIFT_GRID_COLUMNS_INDIVIDUAL : UPLIFT_GRID_COLUMNS;

type QuoteRenewalUpliftOfferingGroupProps = {
  control: Control<QuoteRenewalUpliftFormType>;
  setValue: UseFormSetValue<QuoteRenewalUpliftFormType>;
  offeringIndex: number;
  eligibleOffering: UpliftEligibleOffering;
  items: IQuoteItemWithDisplayType[];
  quote: IQuoteRespSchema;
  quotePrices: IQuotePrice[];
  offeringRate?: IRateResSchema;
  isIndividualMode: boolean;
  isDisabled: boolean;
  /** The offering's uplift is already committed to a generated renewal quote. */
  isLocked: boolean;
  headerAmount: number | null;
};

export const QuoteRenewalUpliftOfferingGroup = ({
  control,
  setValue,
  offeringIndex,
  eligibleOffering,
  items,
  quote,
  quotePrices,
  offeringRate,
  isIndividualMode,
  isDisabled,
  isLocked,
  headerAmount,
}: QuoteRenewalUpliftOfferingGroupProps) => {
  const {
    parentQuoteOffering,
    displayQuoteOffering,
    eligibleProductIds,
    ppuDisplayPrecisionByProductId,
  } = eligibleOffering;
  const products = useWatch({
    control,
    name: `offerings.${offeringIndex}.products`,
  });

  const includedCount = (products ?? []).filter(
    ({ included }) => included,
  ).length;
  const isChecked = !!products?.length && includedCount === products.length;
  const isIndeterminate = includedCount > 0 && !isChecked;

  const handleToggleAll = (isSelected: boolean) => {
    (products ?? []).forEach((_, productIndex) => {
      setValue(
        `offerings.${offeringIndex}.products.${productIndex}.included`,
        isSelected,
        { shouldDirty: true },
      );
    });
  };

  return (
    <MBox
      borderWidth="1px"
      borderColor="tGray.back"
      borderRadius="md"
      bg="tWhite.base"
      px="4"
      py="3"
    >
      <MGrid
        templateColumns={getUpliftGridColumns(isIndividualMode)}
        columnGap="3"
        rowGap="2"
        alignItems="center"
      >
        {/* A uniform uplift covers every product, so there is nothing to
            select — inclusion only exists in individual mode. */}
        {isIndividualMode && (
          <MGridItem>
            <MCheckbox
              isChecked={isChecked}
              isIndeterminate={isIndeterminate}
              isDisabled={isDisabled}
              onChange={({ target }) => handleToggleAll(target.checked)}
              aria-label={`Include ${parentQuoteOffering.offeringName} in renewal uplift`}
              data-testid={`renewal-uplift-offering-checkbox-${parentQuoteOffering.id}`}
            />
          </MGridItem>
        )}
        <MGridItem minW={0} gridColumn={isIndividualMode ? 'span 4' : 'span 3'}>
          <MText fontWeight="semibold" lineClamp={1}>
            {parentQuoteOffering.offeringName}
          </MText>
        </MGridItem>

        {items.map((item) => (
          <QuoteRenewalUpliftProductRow
            key={item.id}
            control={control}
            offeringIndex={offeringIndex}
            productIndex={eligibleProductIds.indexOf(item.productId)}
            item={item}
            quote={quote}
            quotePrices={quotePrices}
            offeringRate={offeringRate}
            parentQuoteOfferingId={parentQuoteOffering.id}
            parentOfferingName={parentQuoteOffering.offeringName}
            rateId={displayQuoteOffering.rateId}
            displayPrecision={ppuDisplayPrecisionByProductId[item.productId]}
            isIndividualMode={isIndividualMode}
            isDisabled={isDisabled}
            isLocked={isLocked}
            headerAmount={headerAmount}
          />
        ))}
      </MGrid>
    </MBox>
  );
};
